import {
  Alert,
  AlertTitle,
  Button,
  Input,
  Link,
  Paragraph,
  Select,
  Spinner,
  Stack,
  Table,
  TableColumns,
  Text,
  Tooltip,
} from '@sixfold/common-ui';
import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';

import { CheckIcon, EmptyState, HealthPill, TourStatusPill } from './ui';
import { useTimeZone } from '../../components/date_formatting/timezone_context';
import { formatDateWithTimeZone } from '../../lib/date';
import { MobileTourPositionsFilter, MobileTourStatusGroup } from '../../lib/graphql';
import { Routes } from '../../routes';
import { Check, Diagnosis, getPrimaryAllocation, Issue } from '../diagnostics';
import { TOURS_PAGE_SIZE, useMobileTours } from '../hooks';

import styles from './mobile.module.css';

type StatusFilter = 'all' | 'ongoing' | 'upcoming' | 'completed' | 'failed';
type HealthFilter = 'all' | 'action';
type PositionsFilter = 'all' | 'received' | 'missing';

export interface ToursView {
  status?: Exclude<StatusFilter, 'all'>;
  health?: Exclude<HealthFilter, 'all'>;
  positions?: Exclude<PositionsFilter, 'all'>;
}

const STATUS_FILTERS: Record<StatusFilter, { label: string; group: MobileTourStatusGroup | null }> = {
  all: { label: 'All statuses', group: null },
  ongoing: { label: 'Ongoing', group: MobileTourStatusGroup.ONGOING },
  upcoming: { label: 'Upcoming', group: MobileTourStatusGroup.UPCOMING },
  completed: { label: 'Completed', group: MobileTourStatusGroup.COMPLETED },
  failed: { label: 'Failed or aborted', group: MobileTourStatusGroup.FAILED },
};

const HEALTH_FILTERS: Record<HealthFilter, string> = {
  all: 'Any health',
  action: 'Needs action',
};

const POSITIONS_FILTERS: Record<PositionsFilter, { label: string; value: MobileTourPositionsFilter | null }> = {
  all: { label: 'Any positions', value: null },
  received: { label: 'Positions received', value: MobileTourPositionsFilter.RECEIVED },
  missing: { label: 'No positions', value: MobileTourPositionsFilter.MISSING },
};

const SEARCH_DEBOUNCE_MS = 400;

const isStatusFilter = (value: string | null): value is StatusFilter => value !== null && value in STATUS_FILTERS;
const isPositionsFilter = (value: string | null): value is PositionsFilter =>
  value !== null && value in POSITIONS_FILTERS;

export const toursViewSearch = (view: ToursView): string => {
  const params = new URLSearchParams();
  Object.entries(view).forEach(([key, value]) => {
    if (value !== undefined) {
      params.set(key, value);
    }
  });
  return params.toString();
};

interface Row {
  id: string;
  health: Diagnosis['health'];
  issue: Issue | null;
  tourId: number;
  transportNumber: string | null;
  carrierId: string | null;
  carrierName: string;
  status: string | null;
  startTime: string;
  plate: string | null;
  checks: Check[];
}

const buildColumns = (showCarrier: boolean): TableColumns<Row> => [
  {
    id: 'health',
    title: 'Health',
    minWidth: '220px',
    cellContent: ({ row }) => (
      <div className={styles.cellStack}>
        <span>
          <HealthPill health={row.health} />
        </span>
        {row.issue !== null && (
          <Text size="small" prominence="subtle">
            {row.issue.title}
          </Text>
        )}
      </div>
    ),
  },
  {
    id: 'tourId',
    title: 'Tour',
    minWidth: '150px',
    cellContent: ({ row }) => (
      <div className={styles.cellStack}>
        <Link href={Routes.MobileTour.generatePath({ tour_id: String(row.tourId) })} weight="bold">
          {row.tourId}
        </Link>
        <Text size="small" prominence="subtle">
          {row.transportNumber ?? 'No transport number'}
        </Text>
      </div>
    ),
  },
  ...(showCarrier
    ? [
        {
          id: 'carrierName' as const,
          title: 'Carrier',
          minWidth: '160px' as const,
          cellContent: ({ row }: { row: Row }) =>
            row.carrierId === null ? (
              row.carrierName
            ) : (
              <Link href={Routes.MobileCompany.generatePath({ company_id: row.carrierId })}>{row.carrierName}</Link>
            ),
        },
      ]
    : []),
  { id: 'status', title: 'Status', cellContent: ({ row }) => <TourStatusPill status={row.status} /> },
  {
    id: 'startTime',
    title: 'Start',
    cellContent: ({ row }) => <span className={styles.mono}>{row.startTime}</span>,
  },
  { id: 'plate', title: 'Vehicle', cellContent: ({ row }) => row.plate ?? '-' },
  {
    id: 'checks',
    title: 'Checklist',
    minWidth: '150px',
    cellContent: ({ row }) => (
      <Stack gap="xsmall" wrap={false}>
        {row.checks.map((check) => (
          <Tooltip key={check.id} content={`${check.label}: ${check.detail}`}>
            <span>
              <CheckIcon status={check.status} />
            </span>
          </Tooltip>
        ))}
      </Stack>
    ),
  },
];

const parsePage = (value: string | null) => {
  const page = parseInt(value ?? '', 10);
  return Number.isNaN(page) || page < 0 ? 0 : page;
};

export const ToursTable: React.FC<{ carrierId?: string }> = ({ carrierId }) => {
  const { timeZone } = useTimeZone();
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get('q') ?? '';
  const statusParam = searchParams.get('status');
  const status: StatusFilter = isStatusFilter(statusParam) ? statusParam : 'all';
  const health: HealthFilter = searchParams.get('health') === 'action' ? 'action' : 'all';
  const positionsParam = searchParams.get('positions');
  const positions: PositionsFilter = isPositionsFilter(positionsParam) ? positionsParam : 'all';
  const page = parsePage(searchParams.get('page'));
  const [searchInput, setSearchInput] = useState(query);

  const setParams = (changes: Record<string, [string, string]>, keepPage = false) => {
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        Object.entries(changes).forEach(([key, [value, defaultValue]]) => {
          if (value === defaultValue) {
            next.delete(key);
          } else {
            next.set(key, value);
          }
        });
        if (!keepPage) {
          next.delete('page');
        }
        return next;
      },
      { replace: true },
    );
  };

  useEffect(() => {
    setSearchInput(query);
  }, [query]);

  useEffect(() => {
    if (searchInput.trim() === query) return undefined;
    const timeout = setTimeout(() => setParams({ q: [searchInput.trim(), ''] }), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  const { rows, count, dataAsOf, loading, error } = useMobileTours({
    carrierId: carrierId ?? null,
    statusGroup: STATUS_FILTERS[status].group,
    positions: POSITIONS_FILTERS[positions].value,
    needsAction: health === 'action',
    search: query,
    page,
  });

  const tableRows: Row[] = useMemo(
    () =>
      rows.map(({ tour, diagnosis }) => ({
        id: String(tour.tour_id),
        health: diagnosis.health,
        issue: diagnosis.issue,
        tourId: tour.tour_id,
        transportNumber: tour.shipper_transport_number ?? tour.transport_id,
        carrierId: tour.carrier?.company_id ?? null,
        carrierName: tour.carrier?.company_name ?? '-',
        status: tour.status,
        startTime: tour.start_time === null ? '-' : formatDateWithTimeZone(tour.start_time, timeZone),
        plate: getPrimaryAllocation(tour.vehicleAllocations)?.vehicleLicensePlateNumber ?? null,
        checks: diagnosis.checks,
      })),
    [rows, timeZone],
  );

  const columns = useMemo(() => buildColumns(carrierId === undefined), [carrierId]);
  const pageCount = Math.max(1, Math.ceil(count / TOURS_PAGE_SIZE));

  return (
    <Stack direction="column" wrap={false} gap="medium">
      <div className={styles.filterBar}>
        <div className={styles.filterSearch}>
          <Input
            label="Search"
            labelPosition="hidden"
            size="small"
            name="mobile-tour-search"
            icon="small-search"
            placeholder="Tour ID, transport number or carrier"
            value={searchInput}
            clearable
            onClear={() => {
              setSearchInput('');
              setParams({ q: ['', ''] });
            }}
            onChange={({ value }) => setSearchInput(value)}
          />
        </div>
        <div className={styles.filterSelect}>
          <Select
            label="Status"
            name="mobile-tour-status"
            value={status}
            options={(Object.keys(STATUS_FILTERS) as StatusFilter[]).map((value) => ({
              value,
              label: STATUS_FILTERS[value].label,
            }))}
            onChange={(option) => setParams({ status: [option?.value ?? 'all', 'all'] })}
          />
        </div>
        <div className={styles.filterSelect}>
          <Select
            label="Health"
            name="mobile-tour-health"
            value={health}
            options={(Object.keys(HEALTH_FILTERS) as HealthFilter[]).map((value) => ({
              value,
              label: HEALTH_FILTERS[value],
            }))}
            onChange={(option) => setParams({ health: [option?.value ?? 'all', 'all'] })}
          />
        </div>
        <div className={styles.filterSelect}>
          <Select
            label="Positions"
            name="mobile-tour-positions"
            value={positions}
            options={(Object.keys(POSITIONS_FILTERS) as PositionsFilter[]).map((value) => ({
              value,
              label: POSITIONS_FILTERS[value].label,
            }))}
            onChange={(option) => setParams({ positions: [option?.value ?? 'all', 'all'] })}
          />
        </div>
        <Text prominence="subtle">
          {count} tours, needing action first
          {dataAsOf === null ? '' : `, data as of ${formatDateWithTimeZone(dataAsOf, timeZone)}`}
        </Text>
      </div>
      {error !== undefined ? (
        <Alert kind="danger">
          <AlertTitle>Could not load tours</AlertTitle>
          <Paragraph>{error.message}</Paragraph>
        </Alert>
      ) : loading ? (
        <Stack justifyContent="center" padding="xlarge">
          <Spinner />
        </Stack>
      ) : tableRows.length === 0 ? (
        <EmptyState
          title="No tours match"
          description="Clear the search or change the status, health and positions filters."
        />
      ) : (
        <>
          <Table data={tableRows} columns={columns} />
          <Stack justifyContent="end" alignItems="center" gap="small">
            <Text prominence="subtle">
              Page {page + 1} of {pageCount}
            </Text>
            <Button
              kind="neutral"
              size="small"
              disabled={page === 0}
              onClick={() => setParams({ page: [String(page - 1), '0'] }, true)}>
              Previous
            </Button>
            <Button
              kind="neutral"
              size="small"
              disabled={page + 1 >= pageCount}
              onClick={() => setParams({ page: [String(page + 1), '0'] }, true)}>
              Next
            </Button>
          </Stack>
        </>
      )}
    </Stack>
  );
};
